// ABOUTME: The talent's job feed as pro-core serves it, typed by hand from TalentJobListingCardHttp, TalentJobListingDetailHttp,
// ABOUTME: TalentJobListingSearchCriteriaHttp, SearchPreferencesHttp and TalentJobListingFilterOptionsHttp, plus the
// ABOUTME: PRO Job Listing additions (PJL, zenjob/pro-core-service#2011): every new field optional until pro-core sends it.
import type {
  AvailabilityDay,
  TimeRange,
  Weekday
} from 'modules/availability/types';

/** Where the work happens. Absent on listings from before PJL: read `remote` then. */
export type WorkplaceMode = 'ON_SITE' | 'REMOTE' | 'HYBRID';

/** By arrangement: the days and hours are agreed each week. Fixed: one window per working day. */
export type WorkingTimeMode = 'BY_ARRANGEMENT' | 'FIXED';

/** How often a job asks for Saturdays, Sundays or public holidays. Absent means unknown, and nothing is shown. */
export type WorkFrequency = 'NONE' | 'OCCASIONAL' | 'REGULAR';

export interface WeekendWork {
  saturday?: WorkFrequency;
  sunday?: WorkFrequency;
  holidays?: WorkFrequency;
  /** The operator's own words, e.g. "etwa 1× im Monat". */
  note?: string;
}

/** GEOFENCE: an extra presence check at check-in and check-out; the talent still reports their hours. */
export type LocationCheck = 'NONE' | 'GEOFENCE';

/** The card's line about the places: how many, the nearest and the farthest from the point searched from (absent without one). */
export interface LocationsSummary {
  count: number;
  city?: string;
  nearestKm?: number;
  farthestKm?: number;
}

/** One place of a listing, for the details' list and map. */
export interface ListingLocation {
  refId: string;
  name: string;
  city?: string;
  lat?: number;
  lon?: number;
  /** Never sent by the detail today: pro-core reads it without a point. */
  distanceKm?: number;
  /** Only on the job of my job (GET /talent/assignments/{ref}/job): where exactly I work, any part may be missing. */
  address?: { street?: string; postalCode?: string; city?: string };
}

/** One window of fixed working time. ISO weekday, Monday = 1; an end before the start runs past midnight. */
export interface WorkingWindow {
  dayOfWeek: number;
  start: string;
  end: string;
}

/** The talent's week against the listing: all of its days, some of them, none, or no week saved to compare. */
export type WeekFitResult = 'FITS' | 'PARTLY' | 'NO';
export type CardWeekFit = WeekFitResult | 'NO_AVAILABILITY';

/** What the details add on their own: the steps of the opening's process, in the talent's words. */
export interface ProcessStepPreview {
  key: string;
  label: string;
  hint?: string | null;
}

/** Requirements pro-core adds by itself: a PRO CV, and location sharing for a listing with a geofence check. */
export type AutoRequirement = 'PRO_CV' | 'LOCATION_SHARING';

/** How my candidacy at the opening behind a listing ended; absent while it is still running. */
export type MyApplicationOutcome = 'HIRED' | 'REJECTED' | 'WITHDRAWN';

/** My own candidacy at the opening behind a listing, whichever wording I came in through. */
export interface MyApplication {
  refId: string;
  outcome?: MyApplicationOutcome;
}

/**
 * One listing as a card in the feed. `companyName` is absent where the channel hides the company, `distanceKm` absent
 * when the search named no point. Money is a number of euros, hours arrive as minutes per week.
 */
export interface JobListingCard {
  refId: string;
  title: string;
  occupationRefId: string;
  occupationName: string;
  companyName?: string;
  locationCity?: string;
  distanceKm?: number;
  hourlyRate: number;
  monthlySalaryMin: number;
  monthlySalaryMax: number;
  minWeeklyHourMinutes: number;
  maxWeeklyHourMinutes: number;
  startDate: string;
  remote: boolean;
  publishedAt: string;
  myApplication?: MyApplication;
  /** PJL: the card's own line of contract, places and badges. */
  companyLogoImageRefId?: string;
  endDate?: string;
  unlimitedContract?: boolean;
  minContractMonths?: number;
  workplaceMode?: WorkplaceMode;
  weekendWork?: WeekendWork;
  /** The feed's WEEKEND_WORK badge, for a card whose weekend details did not come with it. */
  weekendAsked?: boolean;
  locations?: LocationsSummary;
  /** Not sent by pro-core yet (the card badge of PJL-052); the mock leaves it out as well. */
  weekFit?: CardWeekFit;
  /** Set only when the listing shows the flap and one place is left. */
  placesLeft?: number;
  /** Set for 14 days to a talent who saw an earlier version of this listing at a lower rate. Not sent yet (PJL-071). */
  earningsRaised?: { previousHourlyRate: number };
}

export type JobListingSalaryMode = 'FIXED' | 'MIN_BIDDABLE' | 'RANGE_CAPPED';

/**
 * One listing opened from the feed: the card plus the whole render the operator previewed. The location name is absent
 * where the channel hides the location; the point is always there for the map and the commute.
 */
export interface JobListing extends JobListingCard {
  companyLogoImageRefId?: string;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  salaryMode: JobListingSalaryMode;
  minHoursPerDayMinutes?: number;
  /** ISO weekday numbers, Monday = 1 to Sunday = 7. */
  workingDays?: number[];
  workingTimeFrom?: string;
  workingTimeTo?: string;
  endDate?: string;
  unlimitedContract: boolean;
  description?: string;
  requirements?: string;
  otherInformation?: string;
  /** PJL: the working pattern, the places, what applying involves, and the version the talent reads. */
  workingTimeMode?: WorkingTimeMode;
  windows?: WorkingWindow[];
  workLocations?: ListingLocation[];
  locationCheck?: LocationCheck;
  autoRequirements?: AutoRequirement[];
  interviewInfo?: string;
  goodToKnow?: string;
  processSteps?: ProcessStepPreview[];
  versionNo?: number;
  /** False for a listing the talent applied to that was closed or raised since: readable, not open to apply. */
  canApply?: boolean;
}

/** My candidacy at the opening behind a listing, as a filter: NONE is the feed's default, "what is still open to me". */
export enum ApplicationStatusFilter {
  NONE = 'NONE',
  ACTIVE = 'ACTIVE',
  WITHDRAWN = 'WITHDRAWN',
  REJECTED = 'REJECTED',
  HIRED = 'HIRED'
}

/** DISTANCE needs a point; without one the feed is newest first whatever is asked. */
export enum JobListingSort {
  DISTANCE = 'DISTANCE',
  PUBLISHED_AT = 'PUBLISHED_AT'
}

export interface Point {
  lat: number;
  lon: number;
}

/** Everything optional; an absent field narrows nothing. A point and a radius travel together or not at all. */
export interface JobListingSearchCriteria {
  point?: Point;
  radiusKm?: number;
  occupationRefIds?: string[];
  companyRefIds?: string[];
  hourlyRateMin?: number;
  hourlyRateMax?: number;
  query?: string;
  remoteOnly?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
  workingDays?: number[];
  weeklyHoursMinMinutes?: number;
  weeklyHoursMaxMinutes?: number;
  applicationStatus?: ApplicationStatusFilter;
  sort?: JobListingSort;
  /** PJL filters. */
  workplaceModes?: WorkplaceMode[];
  workingTimeModes?: WorkingTimeMode[];
  /** Accepted by pro-core and stored with a saved search, but it narrows nothing until the week fit decides it. */
  fitsMyWeek?: boolean;
}

export interface Pagination {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

/** One page of the search: the cards and meta.pagination, the envelope every paged pro-core answer wears. */
export interface JobListingsPage {
  data: JobListingCard[];
  meta: { pagination: Pagination };
}

export interface JobListingResponse {
  data: JobListing;
}

/**
 * Where pro-core took the job of an application or of my job from: the listing it was made through, the opening behind it
 * when there is no listing, or the assignment itself. The further from a listing, the fewer fields it fills.
 */
export type JobPageSource = 'LISTING' | 'OPENING' | 'ASSIGNMENT';

/**
 * What the job page's header and sections render from: the listing detail's fields, any of them missing. A listing
 * from the feed is one; the job of an application or of my job is one with its `source`.
 */
export type JobDetails = Partial<JobListing> & { source?: JobPageSource };

/**
 * GET /talent/applications/{refId}/job and GET /talent/assignments/{assignmentRefId}/job: the listing detail's shape
 * plus `source`, `canApply` always false. pro-core sends a field it cannot fill as null; the client reads it as absent.
 */
export interface JobPage extends JobDetails {
  source: JobPageSource;
}

export interface JobPageResponse {
  data: JobPage;
}

/** A search a talent named and kept, in the shape the feed accepts. `valid` is read-only and false once it no longer validates. */
export interface SavedSearch {
  refId?: string;
  label: string;
  criteria: JobListingSearchCriteria;
  valid?: boolean;
}

/** A place a talent will travel to. The refId is minted by the server when a new point is posted without one. */
export interface SavedPoint {
  refId?: string;
  label: string;
  lat: number;
  lon: number;
}

/** One document, replaced whole: at most ten searches and three points. */
export interface SearchPreferences {
  savedSearches: SavedSearch[];
  savedPoints: SavedPoint[];
}

export interface SearchPreferencesResponse {
  data: SearchPreferences;
}

/** One thing the feed can be narrowed by: the refId the search takes and the name to show for it. */
export interface FilterOption {
  refId: string;
  name: string;
}

/**
 * GET /talent/jobListings/filterOptions (zenjob/pro-core-service#1515): the occupations and companies with at least
 * one listing I may see on this app today, each ordered by name. A company that hides itself on a listing here is
 * not among them.
 */
export interface FilterOptions {
  occupations: FilterOption[];
  companies: FilterOption[];
}

export interface FilterOptionsResponse {
  data: Partial<FilterOptions>;
}

/** What applying answers: the application's refId, and whether it is new (201) or the one I already had (200). */
export interface ApplyAnswer {
  applicationRefId: string;
  created: boolean;
  /** PJL: what the talent still owes the application; CV while there is no PRO CV. */
  nextAction?: 'CV' | 'NONE';
}

/** The week a talent sends with an application: the listing's days as they stand after editing, hours and start. */
export interface AppliedWeek {
  days: AvailabilityDay[];
  weeklyMinutes?: number;
  startFrom?: string;
  /** Asked only for a job that works on public holidays: whether the talent can too. */
  workOnHolidays?: boolean;
}

/** POST /talent/applications, PJL: the week and what the talent confirmed travel with the application. */
export interface ApplyRequest {
  jobListingRefId: string;
  availability?: AppliedWeek;
  /** Write the edited days back to „Meine Verfügbarkeit“ as well. */
  saveToProfile?: boolean;
  confirmations?: AutoRequirement[];
}

/** POST /talent/jobListings/{refId}/weekFit: without days the profile is read, with them the edited week. */
export interface WeekFitRequest {
  days?: AvailabilityDay[];
  weeklyMinutes?: number;
  /** The earliest start the talent named in the apply flow (ISO day); a start after the job's warns START_DATE. */
  startFrom?: string;
}

/**
 * A hint on top of the days, never a block: WEEKLY_HOURS when the talent's hours a week are below the job's minimum,
 * START_DATE when the talent can only start after the job's start. A result with a warning is never FITS.
 */
export type WeekFitWarning = 'WEEKLY_HOURS' | 'START_DATE';

/** One of the listing's days against the talent's: what the job needs, what the talent has, and whether it is enough. */
export interface WeekFitDay {
  dayOfWeek: Weekday;
  needed: { minMinutes?: number; window?: TimeRange };
  yours: TimeRange[];
  /** The profile's preferred ranges that day, sent back untouched so saving an edited week keeps them. */
  yoursPreferred?: TimeRange[];
  fits: boolean;
  /** Edited in this apply flow, against the profile. */
  changed?: boolean;
}

export interface WeekFitAnswer {
  result: WeekFitResult;
  days: WeekFitDay[];
  weekend?: {
    saturday?: WorkFrequency;
    holidays?: WorkFrequency;
    /** Whether the talent said they can work on public holidays. */
    yoursHolidays?: boolean;
  };
  /** What the profile says, to prefill the hours and the start. */
  profile: { weeklyMinutes?: number; startFrom?: string };
  /** Absent when there is none (decision of 26 Oct 2026). */
  warnings?: WeekFitWarning[];
}

/** POST /talent/jobListings/{refId}/engagements: the apply funnel, one step at a time. */
export type ListingEngagementAction =
  | 'SEEN'
  | 'OPENED'
  | 'APPLY_STARTED'
  | 'APPLY_WEEK_FIT_DONE'
  | 'APPLY_CV_DONE'
  | 'APPLY_REVIEW_OPENED';

/** Where in the app a step was taken: SEEN on a feed card, OPENED on the detail, APPLY_* in the apply flow. */
export type ListingEngagementSource = 'feed' | 'detail' | 'apply';

export type TravelMode = 'TRANSIT' | 'BIKE' | 'CAR';

/** GET /talent/jobListings/{refId}/commute: minutes per place for one mode, asked when the talent opens the places. */
export interface CommuteAnswer {
  mode: TravelMode;
  locations: { refId: string; minutes: number }[];
}
